import { usePersistedState } from "@/lib/persistedFilters";
// Emergency Numbers → Runs. Scans and fixes with their plain-English log; a
// running one is followed live.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Empty, Pill, STATUS, STATUS_ORDER, clock } from "./ui";

const RUN_TONE = { done: "bg-emerald-100 text-emerald-800", running: "bg-sky-100 text-sky-800", failed: "bg-rose-100 text-rose-800" };
const LEVEL = { ERROR: "text-rose-700", WARNING: "text-amber-700", INFO: "text-slate-700" };
const levelOf = (line) => (line.match(/\] \[(ERROR|WARNING|INFO)\]/) || [])[1] || "INFO";

function RunLog({ runId }) {
  const [run, setRun] = useState(null);
  const [level, setLevel] = usePersistedState("e911-runs.level", "all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/e911/runs/${runId}`);
      setRun(data);
    } catch {
      toast.error("Could not load the run");
    }
  }, [runId]);

  useEffect(() => { setRun(null); load(); }, [load]);
  useEffect(() => {
    if (run?.status !== "running") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [run?.status, load]);

  if (!run) return <Loader2 className="h-5 w-5 animate-spin text-slate-300" />;
  const lines = (run.log || []).filter((l) => level === "all" || levelOf(l) === level);
  const s = run.summary || {};
  return (
    <div className="space-y-3" data-testid="e911-run-log">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono text-[12px] text-slate-500">{run.run_id}</span>
        <Pill tone={RUN_TONE[run.status] || "bg-slate-200 text-slate-700"} testid="e911-run-status">{run.status}</Pill>
        <span className="text-slate-700">
          {run.kind === "scan" ? `${run.scope} scan` : `${run.execute ? "EXECUTE" : "dry run"} · ${run.domain}`}
          {run.platforms?.length ? ` · ${run.platforms.join(", ")}` : run.platform ? ` · ${run.platform}` : ""}
        </span>
        <span className="text-[12px] text-slate-500">{clock(run.started_at)} → {run.finished_at ? clock(run.finished_at) : "…"} UTC · {run.by}</span>
        {run.status === "running" && run.progress?.total ? (
          <span className="text-[12px] text-sky-700">{run.progress.done} / {run.progress.total} · {run.stage}</span>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          {[["all", "All"], ["WARNING", `Warnings (${run.warnings || 0})`], ["ERROR", `Errors (${run.errors || 0})`]].map(([v, l]) => (
            <Button key={v} size="sm" variant={level === v ? "default" : "outline"} onClick={() => setLevel(v)}
                    data-testid={`e911-run-level-${v.toLowerCase()}`}>{l}</Button>
          ))}
        </div>
      </div>
      {run.kind === "scan" && !!s.assessed && (
        <div className="flex flex-wrap gap-1.5" data-testid="e911-run-summary">
          <Pill>{s.assessed} assessed</Pill>
          {STATUS_ORDER.filter((k) => s[k]).map((k) => <Pill key={k} tone={STATUS[k].tone}>{s[k]} {STATUS[k].label}</Pill>)}
          {!!s.fixed && <Pill tone="bg-emerald-100 text-emerald-800">{s.fixed} auto-fixed</Pill>}
          {!!s.fix_failed && <Pill tone="bg-rose-100 text-rose-800">{s.fix_failed} fix failed</Pill>}
          {!!s.held_back?.length && <Pill tone="bg-rose-100 text-rose-800">{s.held_back.length} held back</Pill>}
        </div>
      )}
      {run.kind === "fix" && s.result && (
        <div className="flex flex-wrap gap-1.5 text-[12px] text-slate-600" data-testid="e911-run-summary">
          <Pill>{s.result}</Pill>
          {"converted" in s && <span>{s.converted} converted · {s.failed} failed · {s.rolled_back} rolled back · {s.skipped} skipped</span>}
          {s.reason && <span className="text-rose-700">{s.reason}</span>}
        </div>
      )}
      {run.error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{run.error}</div>}
      <pre className="max-h-[32rem] overflow-auto rounded-md border border-slate-200 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-200"
           data-testid="e911-run-lines">
        {lines.length ? lines.map((l, i) => (
          <div key={i} className={levelOf(l) === "INFO" ? "" : levelOf(l) === "ERROR" ? "text-rose-300" : "text-amber-300"}>{l}</div>
        )) : <span className="text-slate-500">{run.log?.length ? "No lines at that level." : "Nothing logged yet."}</span>}
      </pre>
    </div>
  );
}

export default function Runs({ focus, refreshKey, running }) {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("");
  const [selected, setSelected] = useState(focus || "");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/e911/runs", { params: { kind, limit: 60 } });
      setItems(data.items || []);
      setSelected((cur) => cur || data.items?.[0]?.run_id || "");
    } catch {
      toast.error("Could not load the runs");
    }
  }, [kind]);

  useEffect(() => { load(); }, [load, refreshKey, running?.run_id]);
  useEffect(() => { if (focus) setSelected(focus); }, [focus]);

  return (
    <div className="grid gap-4 lg:grid-cols-[22rem_1fr]" data-testid="e911-runs">
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          {[["", "All"], ["scan", "Scans"], ["fix", "Fixes"]].map(([v, l]) => (
            <Button key={v || "all"} size="sm" variant={kind === v ? "default" : "outline"} onClick={() => setKind(v)}
                    data-testid={`e911-runs-kind-${v || "all"}`}>{l}</Button>
          ))}
        </div>
        <Card className="max-h-[36rem] overflow-y-auto">
          <ul className="divide-y divide-slate-100 text-sm" data-testid="e911-runs-list">
            {(items || []).map((r) => (
              <li key={r.run_id}>
                <button type="button" onClick={() => setSelected(r.run_id)} data-testid={`e911-run-${r.run_id}`}
                        className={`w-full px-3 py-2 text-left transition-colors hover:bg-slate-50 ${selected === r.run_id ? "bg-primary/5" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Pill tone={RUN_TONE[r.status] || "bg-slate-200 text-slate-700"}>{r.status}</Pill>
                    <span className="truncate font-medium text-slate-800">
                      {r.kind === "scan" ? `${r.scope} scan` : `${r.execute ? "execute" : "dry run"} · ${r.domain}`}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {clock(r.started_at)} · {r.by}
                    {r.kind === "scan" && r.summary?.assessed ? ` · ${r.summary.assessed} domains` : ""}
                    {r.errors ? <span className="text-rose-600"> · {r.errors} errors</span> : ""}
                  </div>
                </button>
              </li>
            ))}
            {items && !items.length && <li><Empty testid="e911-runs-empty">No runs yet.</Empty></li>}
          </ul>
        </Card>
      </div>
      <Card className="px-4 py-3">
        {selected ? <RunLog runId={selected} /> : <div className="text-sm text-slate-500">Pick a run to read its log.</div>}
      </Card>
    </div>
  );
}
